package org.teddapp.project.ui.today

import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Loan
import com.tedd.db.Loan_payment
import com.tedd.db.Skill
import org.teddapp.project.core.CoreSnapshot
import org.teddapp.project.db.repo.LoanDirection
import org.teddapp.project.db.repo.LoanMath
import org.teddapp.project.ui.finance.fmtMoney
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.skills.DeadlineLabel
import org.teddapp.project.ui.skills.DeadlineTone
import org.teddapp.project.ui.skills.SkillDeadline

/** Une compétence dont la date butoir approche ou est passée, avec ce qu'il reste à faire. */
internal data class DeadlineReminder(val skill: Skill, val label: DeadlineLabel, val text: String)

/**
 * Les rappels à montrer sur Aujourd'hui : dates à quatorze jours ou moins, ou dépassées,
 * les plus pressantes en premier. Les heures restantes viennent du noyau ; sans lui,
 * le rappel ne dit que la date.
 */
internal fun deadlineReminders(skills: List<Skill>, snapshot: CoreSnapshot?, today: String): List<DeadlineReminder> =
    skills
        .filter { it.archived == 0L }
        .mapNotNull { skill ->
            val label = SkillDeadline.label(skill.deadline, today) ?: return@mapNotNull null
            if (!SkillDeadline.needsReminder(label)) return@mapNotNull null
            val hoursLeft = snapshot?.competences?.get(skill.id)?.let { skill.target_hours - it.heures }
            DeadlineReminder(skill, label, SkillDeadline.reminder(label, hoursLeft))
        }
        .sortedBy { it.label.days }

/** Un prêt ouvert dont l'échéance approche ou est passée, avec le restant à régler. */
internal data class LoanReminder(val loan: Loan, val label: DeadlineLabel, val text: String)

/** Les échéances de prêt à rappeler sur Aujourd'hui, même seuil que les dates butoir. */
internal fun loanReminders(loans: List<Loan>, payments: List<Loan_payment>, today: String): List<LoanReminder> =
    loans
        .filter { it.is_deleted == 0L && it.status == "open" }
        .mapNotNull { loan ->
            val label = SkillDeadline.label(loan.due_date, today) ?: return@mapNotNull null
            if (!SkillDeadline.needsReminder(label)) return@mapNotNull null
            val remaining = fmtMoney(LoanMath.remaining(loan, payments), loan.currency, compact = true)
            val side = if (loan.direction == LoanDirection.LENT) "à recevoir" else "à rendre"
            LoanReminder(loan, label, "${label.text} · $remaining $side")
        }
        .sortedBy { it.label.days }

/**
 * La carte des échéances : les dates butoir des compétences, puis les prêts. Un tap sur
 * une compétence ouvre les Compétences, sur un prêt la Finance.
 */
@Composable
internal fun TodayDeadlinesCard(
    reminders: List<DeadlineReminder>,
    onOpen: () -> Unit,
    modifier: Modifier = Modifier,
    loans: List<LoanReminder> = emptyList(),
    onOpenFinance: () -> Unit = {},
) {
    if (reminders.isEmpty() && loans.isEmpty()) return
    TeddCard(modifier, title = "Échéances", icon = "flag", iconColor = TeddTokens.Danger) {
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            reminders.forEach { r -> ReminderLine(r.skill.name, r.text, r.label.tone, onOpen) }
            loans.forEach { r ->
                val who = if (r.loan.direction == LoanDirection.LENT) "Prêt à ${r.loan.counterparty}" else "Dette envers ${r.loan.counterparty}"
                ReminderLine(who, r.text, r.label.tone, onOpenFinance)
            }
        }
    }
}

@Composable
private fun ReminderLine(name: String, text: String, tone: DeadlineTone, onOpen: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onOpen)
            .padding(vertical = 2.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Text(
            text = name,
            color = TeddTokens.Ink,
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium,
            maxLines = 1,
            modifier = Modifier.weight(1f),
        )
        Text(
            text = text,
            style = TeddType.Mono,
            color = if (tone == DeadlineTone.Past) TeddTokens.Danger else TeddTokens.InkMuted,
            fontSize = 11.sp,
        )
    }
}
